// src/Api/apiClient.js
import axios from "axios";
import { WAFEQ_API_URL, WAFEQ_API_KEY, hasWafeqAccess, BASE_API_URL } from "./constants";

const createApiClient = () => {
  if (!hasWafeqAccess()) {
    // throw new Error("WAFEQ_NOT_CONNECTED"); 
    console.warn("Wafeq credentials not found yet.");
  }
//  const { lan } = useLang(); 
  // const lang = texts.en ;

  const lang = localStorage.getItem('language') =='English'? 'en' : 'ar';


  // 1. إنشاء النسخة (Instance)
  const apiClient = axios.create({
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Accept-Language': lang,
    },
  });

  apiClient.interceptors.request.use(
    (config) => {
      if (WAFEQ_API_URL) {
        // Proxy through our backend — attach the customer JWT so the server
        // can authenticate and look up the Wafeq API key from the database.
        config.baseURL = WAFEQ_API_URL;
        const token = localStorage.getItem("token");
        if (token) {
          config.headers["x-t"] = `Bearer ${token}`;
        }
        delete config.headers.Authorization;
      } else {
        config.baseURL = BASE_API_URL;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  // 3. إرجاع نفس النسخة اللي ظبطناها (مهم جداً!)
  return apiClient; 
};
// دالة مساعدة للاستخدام الآني
const getApiClient = () => {
  try {
    return createApiClient();
  } catch (error) {
    if (error.message === "WAFEQ_NOT_CONNECTED" || error.message === "WAFEQ_CREDENTIALS_MISSING") {
      // نرجع promise مرفوض عشان الـ catch يشتغل في المكونات
      return Promise.reject({
        isWafeqError: true,
        message: "لم يتم ربط المنصة بعد. يرجى التواصل مع الدعم الفني.",
        code: "WAFEQ_NOT_CONNECTED"
      });
    }
    throw error;
  }
};

const safeJoin = (value) => {
  return Array.isArray(value) ? value.join(",") : "";
};

const buildQueryString = (params) => {
  const query = new URLSearchParams();

  for (const key in params) {
    const rawValue = params[key];

    if (rawValue === null || rawValue === undefined || rawValue === false) {
      continue;
    }

    if (Array.isArray(rawValue)) {
      if (rawValue.length > 0) {
        query.append(key + "__in", rawValue[0]);
      }
    } else if (typeof rawValue === "boolean") {
      query.append(key, rawValue ? "true" : "false");
    } else {
      query.append(key, String(rawValue));
    }
  }

  return query.toString();
};

// API functions
export const getBranches = () => {
  const apiClient = getApiClient();
  if (apiClient instanceof Promise) return apiClient;
  return apiClient.get("branches/");
};

export const getContacts = () => {
  const apiClient = getApiClient();
  if (apiClient instanceof Promise) return apiClient;
  return apiClient.get("contacts/");
};

export const getProjects = () => {
  const apiClient = getApiClient();
  if (apiClient instanceof Promise) return apiClient;
  return apiClient.get("projects/");
};

export const getCostCenters = () => {
  const apiClient = getApiClient();
  if (apiClient instanceof Promise) return apiClient;
  return apiClient.get("cost-centers/");
};

export const getProfitAndLoss = (filters, config = {}) => {
  const apiClient = getApiClient();
  if (apiClient instanceof Promise) return apiClient;

  const params = {
    date_after: filters.date_after,
    date_before: filters.date_before,
    currency: filters.currency,
    group_by: filters.group_by,
    branch__in: safeJoin(filters.branches),
    cost_center__in: safeJoin(filters.costCenters),
    project__in: safeJoin(filters.projects),
    contact__in: safeJoin(filters.contacts),
    period_count: filters.period_count,
  };
  
  Object.keys(params).forEach((key) => {
    if (
      params[key] === null ||
      params[key] === undefined ||
      params[key] === ""
    ) {
      delete params[key];
    }
  });

  return apiClient.get("reports/profit-and-loss/", { params, ...config });
};

export async function exportProfitAndLoss(filters, format) {
  const apiClient = getApiClient();
  if (apiClient instanceof Promise) return apiClient;

  const query = new URLSearchParams();
  if (filters.date_after) query.append("date_after", filters.date_after);
  if (filters.date_before) query.append("date_before", filters.date_before);
  if (filters.currency) query.append("currency", filters.currency);

  if (filters.group_by) query.append("group_by", filters.group_by);
  if (filters.branches) query.append("branch__in", filters.branches);
  if (filters.projects) query.append("project__in", filters.projects);
  if (filters.costCenters) query.append("cost_center__in", filters.costCenters);
  if (filters.contacts) query.append("contact__in", filters.contacts);
  query.append("export_format", format);

  try {
    const response = await apiClient.get("reports/profit-and-loss/export", {
      params: Object.fromEntries(query.entries()),
    });
    return response;
  } catch (error) {
    console.error("Export failed:", error);
    throw new Error("Failed to export report.", { cause: error.message });
  }
}

export const getCashFlow = (filters, config = {}) => {
  const apiClient = getApiClient();
  if (apiClient instanceof Promise) return apiClient;

  // const costCentersToProcess = filters.costCenters || [];
  const safeBranches = Array.isArray(filters.branches)
    ? filters.branches.join(",")
    : filters.branches;

  const safeProjects = Array.isArray(filters.projects)
    ? filters.projects.join(",")
    : filters.projects;

  // const safeCostCenters = Array.isArray(costCentersToProcess)
  //   ? costCentersToProcess.join(",")
  //   : costCentersToProcess;
  const safeContacts = Array.isArray(filters.contacts)
    ? filters.contacts.join(",")
    : filters.contacts;
  
  const safeCostCenters = Array.isArray(filters.costCenters)
    ? filters.costCenters.join(",")
    : filters.costCenters;

  // Wafeq expects the __in suffix on every dimension filter; the previous bare
  // "branch"/"project" names were silently ignored by the API.
  const params = {
    date_after: filters.date_after,
    date_before: filters.date_before,
    currency: filters.currency,
    branch__in: safeBranches,
    project__in: safeProjects,
    cost_center__in: safeCostCenters || filters.cost_center__in,
    contact__in: safeContacts,
  };
  
  if (filters.group_by) {
    params.group_by = filters.group_by;
  } else {
    delete params.group_by;
  }

  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      delete params[key];
    }
  });

  return apiClient.get("reports/cash-flow/", { params, ...config });
};

export const getBalanceSheet = async (filters, config = {}) => {
  const apiClient = getApiClient();
  if (apiClient instanceof Promise) return apiClient;
  try {
    const response = await apiClient.get("reports/balance-sheet/", {
      params: filters,
      ...config,
    });
    return response;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const getTrialBalance = (filters) => {
  const apiClient = getApiClient();
  if (apiClient instanceof Promise) return apiClient;

  const rawParams = {
    from_date: filters.from_date,
    to_date: filters.to_date,
    ...(filters.include_zero_balances !== undefined && {
      include_zero_balances: filters.include_zero_balances,
    }),
    ...(filters.with_pnl_openings !== undefined && {
      with_pnl_openings: filters.with_pnl_openings,
    }),
    branch: filters.branches,
    cost_center: filters.costCenters,
    project: filters.projects,
  };

  const queryString = buildQueryString(rawParams);
  return apiClient.get(`reports/trial-balance/?${queryString}`);
};

const apiFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: options.body,
  });

  if (url.includes("/export")) {
    return response;
  }

  if (!response.ok) {
    let errorData = {};
    try {
      errorData = await response.json();
    } catch {
      // Ignore
    }

    throw new Error(`HTTP error! status: ${response.status}`, {
      cause: errorData.detail || errorData.message || "Server error",
    });
  }

  return { data: await response.json() };
};

export async function exportTrialBalance(filters, format) {
  const apiClient = getApiClient();
  if (apiClient instanceof Promise) return apiClient;

  const query = new URLSearchParams();
  if (filters.from_date) query.append("from_date", filters.from_date);
  query.append("export_format", format);

  const url = `/v1/reports/trial-balance/export?${query.toString()}`;

  const response = await apiFetch(url);

  if (!response.ok) {
    const errorMessage = `Status ${response.status}: Server responded with an error.`;
    throw new Error("Export request failed.", {
      cause: errorMessage,
      status: response.status,
    });
  }

  const contentType = response.headers.get("content-type");

  if (contentType && contentType.includes("application/json")) {
    return { data: await response.json() };
  } else if (
    contentType &&
    (contentType.includes("application/pdf") ||
      contentType.includes(
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ))
  ) {
    const blob = await response.blob();
    const contentDisposition = response.headers.get("content-disposition");
    let filename = `trial_balance_report.${format}`;
    
    if (contentDisposition && contentDisposition.includes("filename=")) {
      filename = contentDisposition
        .split("filename=")[1]
        .replace(/"/g, "")
        .replace(/;/g, "");
    }

    const fileUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(fileUrl);

    return { data: { message: "File download initiated." } };
  } else {
    throw new Error("Export request failed.", {
      cause: "Received unknown file format from server: " + contentType,
      status: response.status,
    });
  }
}

export const getAccountTransactions = (filters) => {
  const apiClient = getApiClient();
  if (apiClient instanceof Promise) return apiClient;

  const params = {
    date_after: filters.date_after,
    date_before: filters.date_before,
    currency: filters.currency,
    account_id: filters.account_id,
  };

  Object.keys(params).forEach((key) => {
    if (!params[key]) {
      delete params[key];
    }
  });

  return apiClient.get("reports/account-transactions/", { params });
};

export const exportBalanceSheet = async (filters) => {
  const apiClient = getApiClient();
  if (apiClient instanceof Promise) return apiClient;

  try {
    const params = {
      date: filters.date,
      currency: filters.currency,
      export_format: filters.export_format,
    };

    // إضافة الفلاتر الاختيارية
    if (filters.period_count > 0 && filters.group_by) {
      params.period_count = filters.period_count;
      params.group_by = filters.group_by;
    }

    if (filters.branches && filters.branches.length > 0) {
      params.branch__in = Array.isArray(filters.branches)
        ? filters.branches.join(",")
        : filters.branches;
    }

    if (filters.projects && filters.projects.length > 0) {
      params.project__in = Array.isArray(filters.projects)
        ? filters.projects.join(",")
        : filters.projects;
    }

    if (filters.costCenters && filters.costCenters.length > 0) {
      params.cost_center__in = Array.isArray(filters.costCenters)
        ? filters.costCenters.join(",")
        : filters.costCenters;
    }

    // إزالة الحقول الفارغة
    Object.keys(params).forEach((key) => {
      if (
        params[key] === null ||
        params[key] === undefined ||
        params[key] === ""
      ) {
        delete params[key];
      }
    });

    // جرب endpoints مختلفة للتصدير
    let endpoint = "reports/balance-sheet/export";

    // إذا كان الـ endpoint مش شغال، جرب إضافة الـ export_format كبارامتر
    if (!filters.export_format) {
      endpoint = `reports/balance-sheet/`;
      params.export_format = filters.export_format;
    }

    const response = await apiClient.get(endpoint, {
      params,
      responseType: "blob",
    });

    return response;
  } catch (error) {
    console.error("Export balance sheet error:", error);
    throw new Error(`Export failed: ${error.message}`);
  }
};